/**
 * components/admin/UserActionsDropdown.tsx
 *
 * Inline actions dropdown for users on the AdminUsers page.
 * For pending/expired users: generate new link, send by email, copy link.
 * For all users: delete user (with confirmation modal).
 * For active users: "Voir en tant que" to enter impersonation mode.
 * Created: 2026-04-14
 * Updated: 2026-04-14 - added delete user action with ConfirmModal and delete-user edge function.
 * Updated: 2026-04-14 - fixed error message extraction from FunctionsHttpError when invoke
 *   returns non-2xx; reads error body from fnError.context to surface the actual server message.
 * Updated: 2026-04-14 - fixed Response body double-read by cloning before json() attempt;
 *   dev-guarded console.error to avoid exposing internal payloads in production.
 * Updated: 2026-06-09 - added "Voir en tant que" action to enter impersonation mode (active users only).
 * Updated: 2026-06-10 - only navigate on successful impersonation, toast on failure.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import DropdownMenu, { DropdownItem } from '../ui/DropdownMenu';
import { ConfirmModal } from '../ui/ConfirmModal';
import Spinner from '../ui/Spinner';
import { MoreHorizontal, RefreshCw, Mail, Copy, Trash2, Eye } from 'lucide-react';
import { captureError } from '../../utils/errorReporting';

type DisplayStatus = 'active' | 'pending' | 'expired';

interface UserActionsDropdownProps {
  userId: string;
  email: string;
  displayStatus: DisplayStatus;
  onActionComplete: () => void;
}

type LoadingAction = 'email' | 'copy' | 'impersonate' | null;

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  }
}

export default function UserActionsDropdown({
  userId,
  email,
  displayStatus,
  onActionComplete,
}: UserActionsDropdownProps) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { startImpersonation } = useImpersonation();
  const [loading, setLoading] = useState<LoadingAction>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const invokeInvite = async (
    sendEmail: boolean
  ): Promise<{ inviteUrl: string; emailSent: boolean } | null> => {
    const { data, error: fnError } = await supabase.functions.invoke('invite-user', {
      body: { email: email.trim().toLowerCase(), sendEmail },
    });
    if (fnError || !data || data.error) {
      const msg = data?.error || 'Une erreur est survenue. Veuillez réessayer.';
      showToast(msg, 'error');
      return null;
    }
    if (!data.inviteUrl) {
      showToast("Lien d'invitation non reçu. Veuillez réessayer.", 'error');
      return null;
    }
    return { inviteUrl: data.inviteUrl, emailSent: data.emailSent ?? false };
  };

  const handleSendEmail = async () => {
    if (loading) return;
    setLoading('email');
    try {
      const result = await invokeInvite(true);
      if (!result) return;
      if (result.emailSent) {
        showToast('Invitation envoyée par email', 'success');
      } else {
        const copied = await copyToClipboard(result.inviteUrl);
        showToast(
          copied
            ? "Envoi email échoué - lien copié dans le presse-papier"
            : "Envoi email échoué - le lien a été généré mais n'a pas pu être copié",
          'error'
        );
      }
      onActionComplete();
    } finally {
      setLoading(null);
    }
  };

  const handleCopyLink = async () => {
    if (loading) return;
    setLoading('copy');
    try {
      const result = await invokeInvite(false);
      if (!result) return;
      const ok = await copyToClipboard(result.inviteUrl);
      if (ok) {
        showToast('Lien copié dans le presse-papier', 'success');
      } else {
        showToast('Lien généré - impossible de copier automatiquement', 'error');
      }
      onActionComplete();
    } finally {
      setLoading(null);
    }
  };

  const handleImpersonate = async () => {
    if (loading) return;
    setLoading('impersonate');
    try {
      const ok = await startImpersonation(userId);
      if (ok) {
        navigate('/dashboard');
      } else {
        showToast('Impossible de charger le profil utilisateur', 'error');
      }
    } finally {
      setLoading(null);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('delete-user', {
        body: { userId },
      });

      if (fnError || data?.error) {
        let msg = data?.error as string | undefined;
        if (!msg && fnError && 'context' in fnError) {
          try {
            const resp = fnError.context as Response;
            const clone = resp.clone();
            try {
              const body = await clone.json();
              msg = body?.error;
            } catch {
              try {
                msg = await resp.text();
              } catch {
                // ignore
              }
            }
          } catch {
            // ignore
          }
        }
        if (import.meta.env.DEV) {
          console.error('[delete-user] error:', fnError, 'body:', data);
        }
        captureError(fnError ?? new Error(msg ?? 'delete-user failed'), { component: 'UserActionsDropdown', action: 'deleteUser', userId });
        showToast(msg || 'Une erreur est survenue. Veuillez réessayer.', 'error');
        return;
      }

      showToast('Utilisateur supprimé', 'success');
      setIsDeleteOpen(false);
      onActionComplete();
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="inline-flex items-center justify-center w-8 h-8">
        <Spinner size="sm" />
      </div>
    );
  }

  const inviteItems: DropdownItem[] = displayStatus !== 'active'
    ? [
        ...(displayStatus === 'expired'
          ? [
              {
                label: 'Générer un nouveau lien',
                icon: <RefreshCw className="w-4 h-4" />,
                onClick: handleCopyLink,
              } as DropdownItem,
            ]
          : []),
        {
          label: 'Renvoyer par email',
          icon: <Mail className="w-4 h-4" />,
          onClick: handleSendEmail,
        },
        {
          label: 'Copier le lien',
          icon: <Copy className="w-4 h-4" />,
          onClick: handleCopyLink,
        },
        { type: 'separator' } as DropdownItem,
      ]
    : [];

  const impersonateItem: DropdownItem[] = displayStatus === 'active'
    ? [
        {
          label: 'Voir en tant que',
          icon: <Eye className="w-4 h-4" />,
          onClick: handleImpersonate,
        },
        { type: 'separator' } as DropdownItem,
      ]
    : [];

  const items: DropdownItem[] = [
    ...inviteItems,
    ...impersonateItem,
    {
      label: 'Supprimer l\'utilisateur',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: () => setIsDeleteOpen(true),
      variant: 'danger',
    },
  ];

  const trigger = (
    <button
      className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors"
      title="Actions"
    >
      <MoreHorizontal className="w-4 h-4" />
    </button>
  );

  return (
    <>
      <DropdownMenu trigger={trigger} items={items} align="right" />
      <ConfirmModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Supprimer l'utilisateur"
        message={`Êtes-vous sûr de vouloir supprimer le compte de ${email} ? Cette action est irréversible et supprimera définitivement son accès à la plateforme.`}
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        variant="danger"
        loading={isDeleting}
      />
    </>
  );
}
